import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, LogIn, LogOut, Download, FileType, Loader2, FolderOpenDot, Upload } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { clientApi, API, apiError } from "@/lib/api";

const MATTER_STATUS_STYLE = {
  "Open": "bg-[#F5F3EC] text-[#58635C] border-[#E5E1D8]",
  "In Progress": "bg-[#FBF2DE] text-[#8A6D2F] border-[#E8D9B0]",
  "Recovered": "bg-[#E7F0E9] text-[#3F6B4E] border-[#C6DDCC]",
  "Closed": "bg-[#0A0D0B] text-white border-[#0A0D0B]",
};
const money = (v) => `$${Number(v || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ClientPortal() {
  const [client, setClient] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [matters, setMatters] = useState([]);
  const [docs, setDocs] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});

  const loadData = async () => {
    const [m, g] = await Promise.all([clientApi.get("/client/matters"), clientApi.get("/client/generated")]);
    setMatters(m.data.matters); setDocs(g.data.generated);
  };

  const uploadDoc = async (matterId, file) => {
    if (!file) return;
    setUploadingId(matterId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await clientApi.post(`/client/matters/${matterId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded to your file.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setUploadingId(null); }
  };

  useEffect(() => {
    if (!localStorage.getItem("gg_client_token")) { setChecking(false); return; }
    clientApi.get("/client/me").then((r) => { setClient(r.data); return loadData(); })
      .catch(() => localStorage.removeItem("gg_client_token"))
      .finally(() => setChecking(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await clientApi.post("/client/login", { email: email.trim(), password });
      localStorage.setItem("gg_client_token", data.token);
      setClient(data.client);
      await loadData();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const logout = () => { localStorage.removeItem("gg_client_token"); setClient(null); setMatters([]); setDocs([]); };

  return (
    <div className="light-scope min-h-screen flex flex-col">
      <Nav theme="light" />
      <div className="flex-1 max-w-[900px] mx-auto w-full px-5 md:px-10 pt-32 md:pt-40 pb-24">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#58635C] hover:text-[#0A0D0B] transition-colors mb-8">
          <ArrowLeft size={15} /> Back to home
        </Link>

        {checking ? <Loader2 className="animate-spin text-[#B89874]" /> : !client ? (
          <div className="max-w-[460px]">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#B89874]">Client Portal</span>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight text-[#0A0D0B] mt-4 leading-[1.05]">Sign in</h1>
            <p className="mt-5 text-[#58635C] leading-relaxed">View your matters and download letters we've prepared. Ask our office to set up your login if you don't have one yet.</p>
            <form onSubmit={login} data-testid="client-login-form" className="mt-8 bg-white border border-[#E5E1D8] p-6 space-y-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Email</label>
                <input data-testid="client-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Password</label>
                <input data-testid="client-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none" />
              </div>
              <button type="submit" disabled={busy} data-testid="client-login-submit"
                className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <LogIn size={15} />} Sign in
              </button>
            </form>
          </div>
        ) : (
          <div data-testid="client-dashboard">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#B89874]">Welcome</span>
                <h1 className="font-display text-4xl md:text-5xl tracking-tight text-[#0A0D0B] mt-3 leading-[1.05]">{client.name}</h1>
              </div>
              <button onClick={logout} data-testid="client-logout" className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-4 py-2.5 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors"><LogOut size={14} /> Sign out</button>
            </div>

            <h2 className="font-display text-2xl text-[#0A0D0B] mt-12">Your matters</h2>
            {matters.length === 0 ? <p className="mt-3 text-[#58635C]">No matters on file yet.</p> : (
              <div className="mt-4 space-y-2">
                {matters.map((m) => (
                  <div key={m.id} className="flex flex-wrap items-center justify-between gap-3 bg-white border border-[#E5E1D8] px-5 py-4">
                    <div>
                      <div className="font-medium text-[#0A0D0B]">{m.party_name}</div>
                      <div className="text-xs text-[#B89874] font-mono-gg">{m.amount && `Amount: ${m.amount}`} {m.client_ref && `· Ref ${m.client_ref}`}</div>
                      {m.original_amount > 0 && (
                        <div data-testid={`client-balance-${m.id}`} className="mt-1.5 flex flex-wrap gap-x-3 text-xs font-mono-gg">
                          <span className="text-[#3F6B4E]">Paid {money(m.paid)}</span>
                          <span className={m.outstanding > 0 ? "text-[#8A6D2F] font-semibold" : "text-[#3F6B4E] font-semibold"}>
                            {m.outstanding > 0 ? `Outstanding ${money(m.outstanding)}` : "Fully paid"}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-[10px] uppercase tracking-wide px-2.5 py-1 border ${MATTER_STATUS_STYLE[m.status || "Open"]}`}>{m.status || "Open"}</span>
                      <input ref={(el) => (fileRefs.current[m.id] = el)} type="file" className="hidden" data-testid={`client-upload-input-${m.id}`}
                        onChange={(e) => { uploadDoc(m.id, e.target.files[0]); e.target.value = ""; }} />
                      <button onClick={() => fileRefs.current[m.id]?.click()} disabled={uploadingId === m.id} data-testid={`client-upload-${m.id}`}
                        className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-3 py-1.5 text-xs font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors disabled:opacity-60">
                        {uploadingId === m.id ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Send a document
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <h2 className="font-display text-2xl text-[#0A0D0B] mt-12">Your documents</h2>
            {docs.length === 0 ? (
              <div className="mt-3 flex items-center gap-2 text-[#58635C]"><FolderOpenDot size={16} /> No documents available yet.</div>
            ) : (
              <div className="mt-4 space-y-2">
                {docs.map((g) => (
                  <div key={g.id} data-testid={`client-doc-${g.id}`} className="flex flex-wrap items-center justify-between gap-3 bg-white border border-[#E5E1D8] px-5 py-3">
                    <div>
                      <div className="text-sm text-[#0A0D0B]">{g.title} <span className="text-[#58635C]">· {g.party_name}</span></div>
                      <div className="text-xs text-[#B89874] font-mono-gg">Ref {g.reference} · {new Date(g.created_at).toLocaleDateString("en-NZ")}</div>
                    </div>
                    <div className="flex gap-2">
                      <a href={`${API}/generated/${g.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#0A0D0B] text-[#0A0D0B] px-4 py-2 text-sm font-semibold hover:bg-[#0A0D0B] hover:text-white transition-colors"><Download size={14} /> PDF</a>
                      <a href={`${API}/generated/${g.id}/docx`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-4 py-2 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors"><FileType size={14} /> Word</a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
